import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { UniversalPage, UserList, User, Toast, BottomSheet, ButtonList, EditableText, type ButtonListItem } from '../../../shared/ui';
import { useLongPress } from '../../../shared/hooks';
import StatusActionButton from '../../../shared/ui/buttons/StatusActionButton';
import { sitesV2Api, SiteV2, SiteMemberRole, SiteMember } from '../../../api/sitesV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import CreateInviteModal from './CreateInviteModal';
import EditSiteModal from './EditSiteModal';
import ManageMemberPermissions from './ManageMemberPermissions';
import ManageMemberRoles from './ManageMemberRoles';
import { ProfilePage } from '../../profile';
import config from '../../../config/config';
import './SiteInfoPage.css';
import { siteMemberPermissionsApi } from '../../../api/siteMemberPermissionsApi';
import { siteMembersApi } from '../../../api/siteMembersApi';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import { getMediaUrl } from '../../../utils/avatarUtils';
import { openFileInput } from '../../../utils/filePicker';
import { Media } from '../../../api/usersApi';

/** Строка внешнего исполнителя: долгое нажатие — меню (ставка / удаление), как у участников в UserList */
const SiteExternalExecutorRow: React.FC<{
  exec: SiteExecutor;
  canEditFinances: boolean;
  canManageMembers: boolean;
  onOpenActionMenu: (exec: SiteExecutor) => void;
  onDeleteClick: (exec: SiteExecutor) => void;
  onRename: (exec: SiteExecutor, newName: string) => Promise<void>;
}> = ({ exec, canEditFinances, canManageMembers, onOpenActionMenu, onDeleteClick, onRename }) => {
  const hasActionMenu = canEditFinances || canManageMembers;
  const { handlers } = useLongPress({
    onLongPress: hasActionMenu ? () => onOpenActionMenu(exec) : undefined,
    threshold: 500,
    moveThreshold: 10,
    vibrate: true,
  });
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.5rem 0',
        borderBottom: '1px solid var(--theme-border)',
      }}
    >
      <div
        {...(hasActionMenu ? handlers : {})}
        style={{
          flex: 1,
          minWidth: 0,
          cursor: hasActionMenu ? 'pointer' : undefined,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'pan-y',
        }}
      >
        {canManageMembers ? (
          <EditableText
            text={exec.displayName}
            canEdit
            maxLength={200}
            editButtonTitle="Изменить имя"
            placeholder="Имя исполнителя"
            className="site-external-executor-name"
            onSave={(newName) => onRename(exec, newName)}
          />
        ) : (
          <span>{exec.displayName}</span>
        )}
      </div>
      {canManageMembers && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(exec);
          }}
          style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', padding: '0.25rem' }}
          aria-label={`Удалить ${exec.displayName}`}
        >
          <i className="ri-delete-bin-line" />
        </button>
      )}
    </div>
  );
};

interface SiteInfoPageProps {
  isOpen: boolean;
  site: SiteV2 | null;
  siteId: number;
  onClose: () => void;
  onSiteUpdated?: () => void;
  initialMembers?: SiteMember[]; // Передаем участников из родителя, чтобы избежать повторной загрузки
}

const SiteInfoPage: React.FC<SiteInfoPageProps> = ({
  isOpen,
  site,
  siteId,
  onClose,
  onSiteUpdated,
  initialMembers
}) => {
  const { user } = useAuth();
  const { showMenu: showNavMenu, hideMenu: hideNavMenu } = useNavigation();
  const [members, setMembers] = useState<SiteMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<number | null>(null);
  const [showCreateInviteModal, setShowCreateInviteModal] = useState(false);
  const [closingSite, setClosingSite] = useState(false);
  const [reopeningSite, setReopeningSite] = useState(false);
  const [leavingSite, setLeavingSite] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showEditSiteModal, setShowEditSiteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<SiteMember | null>(null);
  const [canManagePermissions, setCanManagePermissions] = useState<boolean>(false);
  const [canManageMembers, setCanManageMembers] = useState<boolean>(false);
  const [canEditSite, setCanEditSite] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const siteAvatarInputRef = useRef<HTMLInputElement>(null);
  const [profileUserId, setProfileUserId] = useState<number | null>(null);
  const [avatarMedia, setAvatarMedia] = useState<Media | null>(null);
  const [allMembersForHistory, setAllMembersForHistory] = useState<SiteMember[]>([]);
  const [externalExecutors, setExternalExecutors] = useState<SiteExecutor[]>([]);
  const [siteExecutorsAll, setSiteExecutorsAll] = useState<SiteExecutor[]>([]);
  const [canEditFinances, setCanEditFinances] = useState(false);
  const [defaultRateExecutor, setDefaultRateExecutor] = useState<SiteExecutor | null>(null);
  const [defaultRateInput, setDefaultRateInput] = useState('');
  const [compensationInput, setCompensationInput] = useState('');
  const [savingDefaultRate, setSavingDefaultRate] = useState(false);
  const [showAddExternalExecutor, setShowAddExternalExecutor] = useState(false);
  const [newExternalExecutorName, setNewExternalExecutorName] = useState('');
  const [addingExternalExecutor, setAddingExternalExecutor] = useState(false);
  const [externalExecutorForMenu, setExternalExecutorForMenu] = useState<SiteExecutor | null>(null);
  const [roleHistoryEvents, setRoleHistoryEvents] = useState<Array<{
    type: 'assigned' | 'revoked';
    date: string;
    memberName: string;
    roleName: string;
    byUserName: string;
  }>>([]);
  const loadingRoleHistoryRef = useRef<boolean>(false);
  const lastLoadedMemberIdsRef = useRef<string>('');

  useEffect(() => {
    if (isOpen) {
      loadData();
      loadAvatarMedia();
    }
  }, [isOpen, siteId]);

  const loadRoleHistory = async (membersList: SiteMember[]) => {
    if (membersList.length === 0) return;
    if (loadingRoleHistoryRef.current) return; // Предотвращаем параллельные вызовы
    
    loadingRoleHistoryRef.current = true;
    try {
      const results = await Promise.allSettled(
        membersList.map((m) => sitesV2Api.getSiteMemberRoleHistory(siteId, m.id))
      );
      const events: Array<{ type: 'assigned' | 'revoked'; date: string; memberName: string; roleName: string; byUserName: string }> = [];
      results.forEach((result, i) => {
        const member = membersList[i];
        const memberName = member?.userName || `Пользователь ${member?.userId ?? ''}`;
        if (result.status !== 'fulfilled' || !member) return;
        const history = result.value;
        history.forEach((record: SiteMemberRole) => {
          events.push({
            type: 'assigned',
            date: record.addedAt,
            memberName,
            roleName: record.roleName || 'Роль',
            byUserName: record.addedByUserName || '—'
          });
          if (record.revokedAt) {
            events.push({
              type: 'revoked',
              date: record.revokedAt,
              memberName,
              roleName: record.roleName || 'Роль',
              byUserName: record.revokedByUserName || '—'
            });
          }
        });
      });
      setRoleHistoryEvents(events);
      // Сохраняем текущий ключ после успешной загрузки
      const currentKey = membersList.length > 0 
        ? membersList.map(m => m.id).sort().join(',')
        : '';
      lastLoadedMemberIdsRef.current = currentKey;
    } catch (err) {
      console.warn('Failed to load role history:', err);
      setRoleHistoryEvents([]);
    } finally {
      loadingRoleHistoryRef.current = false;
    }
  };

  // Эффект для загрузки истории ролей при изменении участников
  useEffect(() => {
    if (!isOpen || !siteId || loading) return;
    
    // Вычисляем текущий ключ участников
    const currentMemberIdsKey = allMembersForHistory.length > 0
      ? allMembersForHistory.map(m => m.id).sort().join(',')
      : '';
    
    // Проверяем, изменился ли набор участников
    const membersChanged = currentMemberIdsKey !== lastLoadedMemberIdsRef.current;

    if (allMembersForHistory.length > 0 && membersChanged) {
      loadRoleHistory(allMembersForHistory);
    } else if (allMembersForHistory.length === 0 && lastLoadedMemberIdsRef.current !== '') {
      // Очищаем только если массив стал пустым и было что-то загружено
      setRoleHistoryEvents([]);
      lastLoadedMemberIdsRef.current = '';
    }
  }, [allMembersForHistory, isOpen, siteId, loading]);

  // Эффект для очистки данных при закрытии модального окна
  useEffect(() => {
    if (!isOpen) {
      setAllMembersForHistory([]);
      setRoleHistoryEvents([]);
      lastLoadedMemberIdsRef.current = '';
    }
  }, [isOpen]);

  const loadAvatarMedia = async () => {
    try {
      const media = await sitesV2Api.getAvatar(siteId);
      setAvatarMedia(media);
    } catch (err) {
      console.warn('Failed to load avatar media:', err);
      setAvatarMedia(null);
    }
  };

  const loadData = async () => {
    try {
      // Если участники переданы из родителя, показываем их сразу (мгновенный показ)
      // Это особенно полезно, если они уже в кеше
      if (initialMembers && initialMembers.length > 0) {
        setMembers(initialMembers);
        setLoading(false);
        // Загружаем все участники (не только активных) в фоне для обновления
        // Используем sitesV2Api чтобы получить роли
        let membersData = initialMembers;
        try {
          membersData = await sitesV2Api.getSiteMembers(siteId);
          setMembers(membersData);
        } catch (err) {
          console.warn('Background load failed:', err);
          // Оставляем начальных участников, если фоновая загрузка не удалась
        }
        try {
          const allForHistory = await siteMembersApi.getSiteMembersIncludingInactive(siteId);
          setAllMembersForHistory(allForHistory);
        } catch (err) {
          console.warn('Failed to load all members for history:', err);
          setAllMembersForHistory(membersData);
        }
        
        // ВАЖНО: Проверяем права пользователя даже при использовании initialMembers
        const currentUserMember = membersData.find(m => m.userId === user?.id);
        
        if (currentUserMember) {
          try {
            const perms = await siteMemberPermissionsApi.getMemberPermissions(siteId, currentUserMember.id);
            const canEdit = perms.some(p => p.permissionCode === 'SITE_EDIT');
            
            // Логируем только если прав нет (для диагностики)
            if (!canEdit) {
              console.warn('SiteInfoPage: Пользователь не имеет права SITE_EDIT. Полученные права:', perms.map(p => p.permissionCode));
            }
            
            setCanManagePermissions(perms.some(p => p.permissionCode === 'SITE_MANAGE_PERMISSION'));
            setCanManageMembers(perms.some(p => p.permissionCode === 'SITE_MANAGE_MEMBERS'));
            setCanEditSite(canEdit);
            setCanEditFinances(perms.some(p => p.permissionCode === 'FINANCE_EDIT'));
          } catch (err) {
            console.warn('Failed to load permissions:', err);
            setCanManagePermissions(false);
            setCanManageMembers(false);
            setCanEditSite(false);
            setCanEditFinances(false);
          }
        } else {
          setCanManagePermissions(false);
          setCanManageMembers(false);
          setCanEditSite(false);
          setCanEditFinances(false);
        }
        try {
          const executors = await siteExecutorsApi.getSiteExecutors(siteId);
          setSiteExecutorsAll(executors);
          setExternalExecutors(executors.filter(e => e.isExternal));
        } catch (err) {
          console.warn('Failed to load external executors:', err);
        }
        return;
      }
      
      setLoading(true);
      setError(null);
      
      // Используем sitesV2Api чтобы получить участников с ролями
      const membersData = await sitesV2Api.getSiteMembers(siteId);
      setMembers(membersData);
      try {
        const allForHistory = await siteMembersApi.getSiteMembersIncludingInactive(siteId);
        setAllMembersForHistory(allForHistory);
      } catch (err) {
        console.warn('Failed to load all members for history:', err);
        setAllMembersForHistory(membersData);
      }

      // Проверяем право SITE_MANAGE_PERMISSION для текущего пользователя
      const currentUserMember = membersData.find(m => m.userId === user?.id);
      
      if (currentUserMember) {
        const perms = await siteMemberPermissionsApi.getMemberPermissions(siteId, currentUserMember.id);
        const canEdit = perms.some(p => p.permissionCode === 'SITE_EDIT');
        
        // Логируем только если прав нет (для диагностики)
        if (!canEdit) {
          console.warn('SiteInfoPage: Пользователь не имеет права SITE_EDIT. Полученные права:', perms.map(p => p.permissionCode));
        }
        
        setCanManagePermissions(perms.some(p => p.permissionCode === 'SITE_MANAGE_PERMISSION'));
        setCanManageMembers(perms.some(p => p.permissionCode === 'SITE_MANAGE_MEMBERS'));
        setCanEditSite(canEdit);
        setCanEditFinances(perms.some(p => p.permissionCode === 'FINANCE_EDIT'));
      } else {
        setCanManagePermissions(false);
        setCanManageMembers(false);
        setCanEditSite(false);
        setCanEditFinances(false);
      }
      try {
        const executors = await siteExecutorsApi.getSiteExecutors(siteId);
        setSiteExecutorsAll(executors);
        setExternalExecutors(executors.filter(e => e.isExternal));
      } catch (err) {
        console.warn('Failed to load external executors:', err);
      }
    } catch (err) {
      console.error('Error loading site info:', err);
      setError('Ошибка загрузки информации о проекте');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    const userIdNumber = parseInt(userId);
    if (userIdNumber === user?.id) {
      setError('Нельзя удалить самого себя');
      return;
    }

    try {
      setRemovingMember(userIdNumber);
      await sitesV2Api.removeSiteMember(siteId, userIdNumber);
      await loadData(); // Перезагружаем данные
    } catch (err) {
      console.error('Ошибка удаления участника:', err);
      setError('Ошибка удаления участника');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleCloseSite = async () => {
    if (!site) return;
    if (!canEditSite) {
      setError('У вас нет прав на закрытие проекта');
      return;
    }
    
    try {
      setClosingSite(true);
      await sitesV2Api.updateSite(siteId, {
        name: site.name,
        closed: true,
        objectAddress: site.objectAddress,
        customerName: site.customerName
      });
      onSiteUpdated?.();
      onClose();
    } catch (err) {
      console.error('Ошибка закрытия проекта:', err);
      setError('Ошибка закрытия проекта');
    } finally {
      setClosingSite(false);
    }
  };

  const handleReopenSite = async () => {
    if (!site) return;
    if (!canEditSite) {
      setError('У вас нет прав на возобновление проекта');
      return;
    }
    
    try {
      setReopeningSite(true);
      await sitesV2Api.updateSite(siteId, {
        name: site.name,
        closed: false,
        objectAddress: site.objectAddress,
        customerName: site.customerName
      });
      onSiteUpdated?.();
      onClose();
    } catch (err) {
      console.error('Ошибка возобновления проекта:', err);
      setError('Ошибка возобновления проекта');
    } finally {
      setReopeningSite(false);
    }
  };

  const handleLeaveSite = async () => {
    if (!site) return;
    
    try {
      setLeavingSite(true);
      setError(null);
      await siteMembersApi.leaveSite(siteId);
      // После покидания объекта закрываем модальное окно и обновляем список объектов
      onSiteUpdated?.();
      onClose();
    } catch (err: any) {
      console.error('Ошибка покидания объекта:', err);
      setError(err.response?.data?.message || 'Ошибка покидания объекта');
    } finally {
      setLeavingSite(false);
      setShowMenu(false);
    }
  };

  const handleMenuClick = () => {
    setShowMenu(true);
  };

  const handleMenuClose = () => {
    setShowMenu(false);
  };

  // Глобальная кнопка «три точки» в окне информации об объекте: открывает «Действия с объектом»; при закрытии нижнего окна с профилем меню восстанавливается
  useEffect(() => {
    if (isOpen && profileUserId === null) {
      showNavMenu(handleMenuClick);
    }
    return () => {
      hideNavMenu();
    };
  }, [isOpen, profileUserId, showNavMenu, hideNavMenu]);

  const handleSiteAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !site) return;

    try {
      setUploadingAvatar(true);
      setError(null);
      await sitesV2Api.uploadAvatar(siteId, file);
      const updatedSite = await sitesV2Api.getSite(siteId, false);
      if (site) {
        site.avatarUrl = updatedSite.avatarUrl;
      }
      await loadData();
      await loadAvatarMedia();
      if (onSiteUpdated) {
        onSiteUpdated();
      }
    } catch (err: any) {
      console.error('Ошибка загрузки аватарки:', err);
      setError(err.response?.data?.message || 'Ошибка загрузки фотографии объекта');
    } finally {
      setUploadingAvatar(false);
      setShowMenu(false);
      if (siteAvatarInputRef.current) {
        siteAvatarInputRef.current.value = '';
      }
    }
  };

  const handleUploadAvatar = () => {
    if (!site) return;
    openFileInput(siteAvatarInputRef.current);
  };

  const getMenuButtonItems = (): ButtonListItem[] => {
    const closeAnd = (fn: () => void | Promise<void>) => () => {
      handleMenuClose();
      fn();
    };
    const items: ButtonListItem[] = [];

    // Пункты меню для пользователей с правом SITE_EDIT
    if (canEditSite) {
      items.push({
        id: 'edit-site',
        label: 'Редактировать объект',
        icon: 'ri-pencil-line',
        onClick: closeAnd(() => setShowEditSiteModal(true)),
        disabled: false
      });
      items.push({
        id: 'upload-avatar',
        label: 'Загрузить фотографию объекта',
        icon: 'ri-image-add-line',
        onClick: closeAnd(handleUploadAvatar),
        disabled: uploadingAvatar
      });
      items.push({
        id: 'close-site',
        label: site?.closed ? 'Возобновить проект' : 'Закрыть проект',
        icon: site?.closed ? 'ri-play-circle-line' : 'ri-stop-circle-line',
        onClick: closeAnd(site?.closed ? handleReopenSite : handleCloseSite),
        disabled: site?.closed ? reopeningSite : closingSite,
        variant: site?.closed ? 'default' : 'danger'
      });
    }

    // Пункт "Покинуть объект" доступен всем участникам
    items.push({
      id: 'leave-site',
      label: 'Покинуть объект',
      icon: 'ri-logout-box-line',
      onClick: closeAnd(handleLeaveSite),
      disabled: leavingSite,
      variant: 'danger'
    });

    return items;
  };

  const getAvatarUrl = (): string | undefined => {
    // Для обложки используем полный размер (full) из медиа объекта
    if (avatarMedia) {
      const fullUrl = getMediaUrl(avatarMedia, 'full');
      if (fullUrl) return fullUrl;
    }
    
    // Fallback на avatarUrl из объекта (может быть меньшего размера)
    if (!site?.avatarUrl || !site.avatarUrl.trim()) return undefined;
    const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
    const url = site.avatarUrl.startsWith('http') ? site.avatarUrl : `${apiBaseUrl}${site.avatarUrl}`;
    return url.trim() || undefined;
  };

  const handleManagePermissions = (member: SiteMember) => {
    console.log('handleManagePermissions called for:', member);
    console.log('siteId:', siteId, 'member.id:', member.id);
    setSelectedMember(member);
    setShowPermissionsModal(true);
    console.log('showPermissionsModal set to true, selectedMember:', member);
  };

  const handleManageRoles = (member: SiteMember) => {
    console.log('handleManageRoles called for:', member);
    setSelectedMember(member);
    setShowRolesModal(true);
  };

  const handleViewProfile = (userId: number) => {
    setProfileUserId(userId);
  };

  const handleRenameExternalExecutor = useCallback(async (exec: SiteExecutor, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === exec.displayName.trim()) return;
    await siteExecutorsApi.updateExternalExecutor(siteId, exec.id, trimmed);
    const applyName = (prev: SiteExecutor[]) =>
      prev.map((e) => (e.id === exec.id ? { ...e, displayName: trimmed } : e));
    setExternalExecutors(applyName);
    setSiteExecutorsAll(applyName);
    setExternalExecutorForMenu((prev) =>
      prev?.id === exec.id ? { ...prev, displayName: trimmed } : prev
    );
  }, [siteId]);

  const confirmDeleteExternalExecutor = useCallback(async (exec: SiteExecutor) => {
    if (!window.confirm(`Удалить внешнего исполнителя «${exec.displayName}»?`)) return;
    try {
      await siteExecutorsApi.deleteExternalExecutor(siteId, exec.id);
      setExternalExecutors((prev) => prev.filter((e) => e.id !== exec.id));
      setSiteExecutorsAll((prev) => prev.filter((e) => e.id !== exec.id));
      setExternalExecutorForMenu(null);
    } catch (err) {
      console.error('Error deleting external executor:', err);
      setError('Ошибка удаления');
    }
  }, [siteId]);

  const parseMoneyInput = (raw: string): { ok: boolean; value: number | null } => {
    const trimmed = raw.trim();
    if (trimmed === '') return { ok: true, value: null };
    const parsed = parseFloat(trimmed.replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) return { ok: false, value: null };
    return { ok: true, value: parsed };
  };

  const handleSaveExecutorDefaultRate = async () => {
    if (!defaultRateExecutor) return;
    const rate = parseMoneyInput(defaultRateInput);
    const compensation = parseMoneyInput(compensationInput);
    if (!rate.ok || !compensation.ok) {
      setError('Введите неотрицательное число или оставьте поле пустым');
      return;
    }
    try {
      setSavingDefaultRate(true);
      setError(null);
      await siteExecutorsApi.updateExecutorDefaultHourlyRate(siteId, defaultRateExecutor.id, rate.value);
      await siteExecutorsApi.updateExecutorCompensationPerVisit(siteId, defaultRateExecutor.id, compensation.value);
      const executors = await siteExecutorsApi.getSiteExecutors(siteId);
      setSiteExecutorsAll(executors);
      setExternalExecutors(executors.filter(e => e.isExternal));
      setDefaultRateExecutor(null);
      setDefaultRateInput('');
      setCompensationInput('');
    } catch (err) {
      console.error(err);
      setError('Не удалось сохранить ставки исполнителя');
    } finally {
      setSavingDefaultRate(false);
    }
  };

  const isCreator = (userId: number) => {
    // Предполагаем, что создатель - это пользователь с определенной ролью или первый участник
    return members.find(member => member.userId === userId)?.roleName?.toLowerCase().includes('создатель') || false;
  };

  const getMemberUsers = (): User[] => {
    const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
    return members.map(member => {
      const executorForMember = siteExecutorsAll.find(e => e.userId === member.userId);
      return {
        userId: member.userId.toString(),
        userName: member.userName || `Пользователь ${member.userId}`,
        roles: member.roles && member.roles.length > 0
          ? member.roles.filter((r: SiteMemberRole) => r.isActive).map((r: SiteMemberRole) => ({
              id: r.id,
              roleName: r.roleName,
              roleId: r.roleId,
              hierarchyLevel: r.hierarchyLevel 
            }))
          : undefined,
        userRole: member.roleName || 'Участник',
        isCreator: isCreator(member.userId),
        avatarUrl: member.avatarUrl 
          ? (member.avatarUrl.startsWith('http') ? member.avatarUrl : `${apiBaseUrl}${member.avatarUrl}`)
          : undefined,
        onManagePermissions: () => handleManagePermissions(member),
        onManageRoles: () => handleManageRoles(member),
        onViewProfile: () => handleViewProfile(member.userId),
        ...(canEditFinances && executorForMember
          ? {
              onConfigureDefaultRate: () => {
                setDefaultRateExecutor(executorForMember);
                setDefaultRateInput(
                  executorForMember.defaultHourlyRate != null && executorForMember.defaultHourlyRate !== undefined
                    ? String(executorForMember.defaultHourlyRate)
                    : ''
                );
                setCompensationInput(
                  executorForMember.compensationPerVisit ? String(executorForMember.compensationPerVisit) : ''
                );
              }
            }
          : {}),
      };
    });
  };

  type HistoryEvent =
    | { kind: 'join'; date: string; memberName: string; byUserName: string }
    | { kind: 'exclude'; date: string; memberName: string; byUserName: string }
    | { kind: 'role_assigned'; date: string; memberName: string; roleName: string; byUserName: string }
    | { kind: 'role_revoked'; date: string; memberName: string; roleName: string; byUserName: string };

  const unifiedHistory = useMemo(() => {
    const events: HistoryEvent[] = [];
    allMembersForHistory.forEach((m) => {
      const name = m.userName || `Пользователь ${m.userId}`;
      events.push({
        kind: 'join',
        date: m.joinedAt,
        memberName: name,
        byUserName: m.addedByUserName || '—'
      });
      if (m.leftAt) {
        events.push({
          kind: 'exclude',
          date: m.leftAt,
          memberName: name,
          byUserName: m.removedByUserName || '—'
        });
      }
    });
    roleHistoryEvents.forEach((e) => {
      events.push(
        e.type === 'assigned'
          ? { kind: 'role_assigned', date: e.date, memberName: e.memberName, roleName: e.roleName, byUserName: e.byUserName }
          : { kind: 'role_revoked', date: e.date, memberName: e.memberName, roleName: e.roleName, byUserName: e.byUserName }
      );
    });
    events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return events;
  }, [allMembersForHistory, roleHistoryEvents]);

  const historyByDate = useMemo(() => {
    const groups: { dateKey: string; dateLabel: string; events: HistoryEvent[] }[] = [];
    const map = new Map<string, HistoryEvent[]>();
    unifiedHistory.forEach((event) => {
      const d = new Date(event.date);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(event);
    });
    const sortedKeys = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));
    sortedKeys.forEach((dateKey) => {
      const [y, m, d] = dateKey.split('-');
      const dateLabel = new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      groups.push({ dateKey, dateLabel, events: map.get(dateKey)! });
    });
    return groups;
  }, [unifiedHistory]);

  if (!site || !isOpen) {
    return null;
  }

  const avatarUrl = getAvatarUrl();

  return (
    <>
      <input
        ref={siteAvatarInputRef}
        type="file"
        accept="image/*"
        onChange={handleSiteAvatarFileChange}
        style={{ display: 'none' }}
        aria-hidden
        tabIndex={-1}
      />

      <UniversalPage
        isOpen={isOpen}
        title={site.name}
        onClose={onClose}
        centered={true}
        className="site-info-page"
        coverImage={avatarUrl ?? undefined}
        avatar={avatarUrl ? {
          imageUrl: avatarUrl,
          icon: site.closed ? 'ri-archive-line' : 'ri-building-2-line',
          color: site.closed ? 'var(--theme-text-muted)' : 'var(--theme-primary)'
        } : undefined}
        onMenu={handleMenuClick}
        showMenu={true}
        menuButtonRef={menuButtonRef}
      >

      {error && (
        <Toast
          id="site-info-error"
          message={error}
          type="error"
          duration={4000}
          onClose={() => setError(null)}
        />
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner">
            <i className="ri-loader-4-line ri-spin"></i>
          </div>
          <span>Загрузка информации...</span>
        </div>
      ) : (
        <>
          {/* Зона информации об объекте: адрес приходит с сервера только для участников, имя заказчика — всем */}
          {(site.objectAddress?.trim() || site.customerName?.trim()) && (
            <section className="site-info-block">
              <div className="site-info-block__row">
                {site.objectAddress?.trim() && (
                  <div className="site-info-block__item">
                    <i className="ri-map-pin-line site-info-block__icon" aria-hidden />
                    <span className="site-info-block__value">{site.objectAddress.trim()}</span>
                  </div>
                )}
                {site.customerName?.trim() && (
                  <div className="site-info-block__item">
                    <i className="ri-user-line site-info-block__icon" aria-hidden />
                    <span className="site-info-block__value">{site.customerName.trim()}</span>
                  </div>
                )}
              </div>
            </section>
          )}

          <UserList
            users={getMemberUsers()}
            onAddUser={() => setShowCreateInviteModal(true)}
            onRemoveUser={handleRemoveMember}
            canRemoveUser={(userId) => {
              const userIdNumber = parseInt(userId);
              return userIdNumber !== user?.id; // Нельзя удалить самого себя
            }}
            addButtonText="Пригласить участника"
            showRemoveButton={false}
            canManagePermissions={canManagePermissions}
            canManageMembers={canManageMembers}
          />

          {/* Внешние исполнители: ставка по умолчанию — долгое нажатие на строку, как у участников */}
          {(canManageMembers || canEditFinances) && (
            <section className="site-info-block" style={{ marginTop: '1rem' }}>
              <h3 className="site-info-history__title" style={{ marginBottom: '0.5rem' }}>
                <i className="ri-user-unfollow-line" aria-hidden />
                Внешние исполнители
              </h3>
              <p className="site-info-block__value" style={{ fontSize: '0.875rem', color: 'var(--theme-text-muted)', marginBottom: '0.75rem' }}>
                Исполнители без учётной записи в системе (подрядчики, разовые работники). Имя можно изменить кнопкой карандаша. Долгое нажатие — меню: ставка по умолчанию и удаление.
              </p>
              {externalExecutors.map((exec) => (
                <SiteExternalExecutorRow
                  key={exec.id}
                  exec={exec}
                  canEditFinances={canEditFinances}
                  canManageMembers={canManageMembers}
                  onOpenActionMenu={setExternalExecutorForMenu}
                  onDeleteClick={confirmDeleteExternalExecutor}
                  onRename={handleRenameExternalExecutor}
                />
              ))}
              {canManageMembers && showAddExternalExecutor ? (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={newExternalExecutorName}
                    onChange={(e) => setNewExternalExecutorName(e.target.value)}
                    placeholder="Имя исполнителя"
                    style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--theme-border)' }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!newExternalExecutorName.trim()) return;
                      try {
                        setAddingExternalExecutor(true);
                        const created = await siteExecutorsApi.createExternalExecutor(siteId, newExternalExecutorName.trim());
                        setExternalExecutors((prev) => [...prev, created]);
                        setSiteExecutorsAll((prev) => [...prev, created]);
                        setNewExternalExecutorName('');
                        setShowAddExternalExecutor(false);
                      } catch (err) {
                        console.error('Error creating external executor:', err);
                        setError('Ошибка создания');
                      } finally {
                        setAddingExternalExecutor(false);
                      }
                    }}
                    disabled={addingExternalExecutor || !newExternalExecutorName.trim()}
                    style={{ padding: '0.5rem 1rem', borderRadius: '4px', background: 'var(--theme-primary)', color: 'white', border: 'none', cursor: 'pointer' }}
                  >
                    {addingExternalExecutor ? '...' : 'Добавить'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddExternalExecutor(false); setNewExternalExecutorName(''); }}
                    style={{ padding: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--theme-text-muted)' }}
                  >
                    Отмена
                  </button>
                </div>
              ) : canManageMembers ? (
                <button
                  type="button"
                  onClick={() => setShowAddExternalExecutor(true)}
                  style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'none', border: '1px dashed var(--theme-border)', borderRadius: '4px', cursor: 'pointer', color: 'var(--theme-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                >
                  <i className="ri-add-line" /> Добавить внешнего исполнителя
                </button>
              ) : null}
            </section>
          )}

          {/* Объединённая история по датам: вступления, исключения, изменения ролей */}
          <section className="site-info-history">
            <h3 className="site-info-history__title">
              <i className="ri-history-line" aria-hidden />
              История
            </h3>
            {historyByDate.map(({ dateKey, dateLabel, events }) => (
              <div key={dateKey} className="site-info-history__day">
                <div className="site-info-history__date">{dateLabel}</div>
                <ul className="site-info-history__list">
                  {events.map((event, idx) => {
                    let displayName: string;
                    let meta: string;
                    if (event.kind === 'join') {
                      displayName = event.memberName;
                      meta = event.byUserName !== '—' ? ` вступил, пригласил ${event.byUserName}` : ' вступил в объект';
                    } else if (event.kind === 'exclude') {
                      displayName = event.memberName;
                      meta = event.byUserName !== '—' ? ` исключён, исключил ${event.byUserName}` : ' исключён из объекта';
                    } else if (event.kind === 'role_assigned') {
                      displayName = event.byUserName;
                      meta = ` назначил роль «${event.roleName}» для ${event.memberName}`;
                    } else {
                      displayName = event.memberName;
                      meta = ` роль «${event.roleName}» отозвана, отозвал ${event.byUserName}`;
                    }
                    return (
                      <li
                        key={`${event.kind}-${event.date}-${event.memberName}-${idx}`}
                        className={`site-info-history__item site-info-history__item_${event.kind}`}
                      >
                        <span className="site-info-history__line">
                          {displayName}
                          {meta}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>
        </>
      )}

      {/* Модальное окно создания приглашения */}
      <CreateInviteModal
        isOpen={showCreateInviteModal}
        siteId={siteId}
        onClose={() => setShowCreateInviteModal(false)}
      />

      {/* Модальное окно управления правами */}
      <ManageMemberPermissions
        isOpen={showPermissionsModal && selectedMember !== null}
        onClose={() => {
          setShowPermissionsModal(false);
          setSelectedMember(null);
        }}
        siteId={siteId}
        siteMemberId={selectedMember?.id || 0}
        userName={selectedMember?.userName || `Пользователь ${selectedMember?.userId || ''}`}
        userRole={selectedMember?.roleName || 'Участник'}
      />

      {/* Модальное окно управления ролями */}
      <ManageMemberRoles
        isOpen={showRolesModal && selectedMember !== null}
        onClose={() => {
          setShowRolesModal(false);
          setSelectedMember(null);
        }}
        siteId={siteId}
        siteMemberId={selectedMember?.id || 0}
        userName={selectedMember?.userName || `Пользователь ${selectedMember?.userId || ''}`}
        onRolesUpdated={() => {
          loadData(); // Перезагружаем данные
        }}
      />

      {/* Редактирование объекта: название, заказчик, адрес */}
      <EditSiteModal
        isOpen={showEditSiteModal}
        site={site}
        siteId={siteId}
        onClose={() => setShowEditSiteModal(false)}
        onSuccess={() => {
          loadData();
          onSiteUpdated?.();
        }}
      />

      <BottomSheet
        isOpen={defaultRateExecutor !== null}
        onClose={() => {
          setDefaultRateExecutor(null);
          setDefaultRateInput('');
          setCompensationInput('');
        }}
        title="Ставки исполнителя"
        size="small"
        showHandle
      >
        {defaultRateExecutor && (
          <div style={{ padding: '0 0 0.5rem' }}>
            <p style={{ marginBottom: '0.75rem', color: 'var(--theme-text-muted)', fontSize: '0.875rem', lineHeight: 1.45 }}>
              <strong>{defaultRateExecutor.userName || defaultRateExecutor.displayName}</strong>
              {' — '}₽/ч для вида работ «Не указано» (в том числе при массовом открытии смен). Пустое поле — в активности будет 0 ₽/ч, пока не задана ставка на объекте.
            </p>
            <input
              id="executor-default-rate"
              type="number"
              aria-label="Ставка в рублях за час"
              min={0}
              step={0.01}
              inputMode="decimal"
              value={defaultRateInput}
              onChange={(e) => setDefaultRateInput(e.target.value)}
              placeholder="Не задано (0 ₽/ч)"
              style={{
                width: '100%',
                padding: '0.65rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--theme-border)',
                marginBottom: '1rem',
                fontSize: '1rem',
              }}
            />
            <p style={{ marginBottom: '0.75rem', color: 'var(--theme-text-muted)', fontSize: '0.875rem', lineHeight: 1.45 }}>
              Компенсация за выезд, ₽ за смену — начисляется автоматически при закрытии смены (тип «Компенсация», не зависит от часов). Пустое поле — не начисляется.
            </p>
            <input
              id="executor-compensation-per-visit"
              type="number"
              aria-label="Компенсация за выезд в рублях за смену"
              min={0}
              step={0.01}
              inputMode="decimal"
              value={compensationInput}
              onChange={(e) => setCompensationInput(e.target.value)}
              placeholder="Не начисляется"
              style={{
                width: '100%',
                padding: '0.65rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--theme-border)',
                marginBottom: '1rem',
                fontSize: '1rem',
              }}
            />
            <StatusActionButton
              variant="primary"
              fullWidth
              loading={savingDefaultRate}
              onClick={handleSaveExecutorDefaultRate}
              icon="ri-check-line"
            >
              Сохранить
            </StatusActionButton>
          </div>
        )}
      </BottomSheet>

      <BottomSheet
        isOpen={externalExecutorForMenu !== null}
        onClose={() => setExternalExecutorForMenu(null)}
        title={externalExecutorForMenu?.displayName ?? 'Действия'}
        size="small"
      >
        <ButtonList
          spacing="normal"
          showDividers
          items={[
            ...(canEditFinances && externalExecutorForMenu
              ? [
                  {
                    id: 'external-default-rate',
                    icon: 'ri-money-ruble-circle-line' as const,
                    label: 'Настроить ставку по умолчанию',
                    onClick: () => {
                      const e = externalExecutorForMenu;
                      setExternalExecutorForMenu(null);
                      if (e) {
                        setDefaultRateExecutor(e);
                        setDefaultRateInput(
                          e.defaultHourlyRate != null && e.defaultHourlyRate !== undefined
                            ? String(e.defaultHourlyRate)
                            : ''
                        );
                        setCompensationInput(
                          e.compensationPerVisit ? String(e.compensationPerVisit) : ''
                        );
                      }
                    },
                  },
                ]
              : []),
            ...(canManageMembers && externalExecutorForMenu
              ? [
                  {
                    id: 'external-delete',
                    icon: 'ri-delete-bin-line' as const,
                    label: 'Удалить исполнителя',
                    variant: 'danger' as const,
                    onClick: () => {
                      const e = externalExecutorForMenu;
                      setExternalExecutorForMenu(null);
                      if (e) void confirmDeleteExternalExecutor(e);
                    },
                  },
                ]
              : []),
          ]}
        />
      </BottomSheet>
      </UniversalPage>

      {/* Меню действий — боттомшит вместо выпадающего меню */}
      <BottomSheet
        isOpen={showMenu}
        title="Действия с объектом"
        onClose={handleMenuClose}
        size="auto"
      >
        <ButtonList
          items={getMenuButtonItems()}
          spacing="normal"
          showDividers
        />
      </BottomSheet>

      {/* Боттомшит профиля участника — elevated, чтобы всегда поверх окна информации */}
      <BottomSheet
        isOpen={profileUserId !== null}
        onClose={() => setProfileUserId(null)}
        size="fullscreen"
        elevated
      >
        <div className="site-info-profile-sheet">
          {profileUserId != null && (
            <ProfilePage
              userId={profileUserId}
              hideHeader
              onBack={() => setProfileUserId(null)}
            />
          )}
        </div>
      </BottomSheet>
    </>
  );
};

export default SiteInfoPage;
